/*
 * Pure helpers for the manual-receipt create form (ADR-0031, US-20).
 * No React, no I/O — unit-tested in manualReceipt.test.ts.
 *
 * Amount parsing accepts the Ukrainian decimal comma («250,00») as well as a
 * dot, mirroring what the user types under the ru/uk keyboard locales. All
 * summing is done in integer cents to avoid float drift across many lines
 * (same discipline as ReceiptDetailScreen's «Разом»).
 */

export interface ManualLineDraft {
  /** Stable client-side row id (never sent to the server). */
  id: string;
  name: string;
  /** Raw amount string as typed (may use a comma decimal). */
  amount: string;
  /** Quantity, defaults to 1. */
  qty: number;
}

/**
 * Parses a user-typed amount string to a number, or `null` when it is empty /
 * not a finite positive number. Accepts a comma or dot decimal separator and
 * ignores surrounding whitespace.
 */
export function parseManualAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** A draft line is valid when it has a non-empty name and a positive amount. */
export function isValidLine(line: ManualLineDraft): boolean {
  return line.name.trim() !== "" && parseManualAmount(line.amount) !== null;
}

/** A row the user started but left half-filled (exactly one of name/amount). */
export function isPartialLine(line: ManualLineDraft): boolean {
  const hasName = line.name.trim() !== "";
  const hasAmount = parseManualAmount(line.amount) !== null;
  return hasName !== hasAmount;
}

/**
 * Running total of the draft in integer cents (qty × amount over valid rows).
 * Rows that are not yet valid contribute 0.
 */
export function draftTotalCents(lines: ReadonlyArray<ManualLineDraft>): number {
  return lines.reduce((acc, l) => {
    const amount = parseManualAmount(l.amount);
    if (amount === null) return acc;
    const qty = l.qty > 0 ? l.qty : 1;
    return acc + Math.round(amount * 100) * qty;
  }, 0);
}

export interface CreateGateInput {
  vendorName: string;
  payerId: string | null;
  lines: ReadonlyArray<ManualLineDraft>;
}

/**
 * The create gate (AC-20.2/.3/.4/.5): a non-empty label, a chosen payer, at
 * least one valid line, and no half-filled rows. Currency is always set (the
 * form defaults it to the trip settlement ccy) so it is not gated here.
 */
export function canCreateManual({
  vendorName,
  payerId,
  lines,
}: CreateGateInput): boolean {
  if (vendorName.trim() === "") return false;
  if (payerId == null) return false;
  if (lines.some(isPartialLine)) return false;
  return lines.some(isValidLine);
}

/**
 * The first unmet requirement, for the caption under a disabled «Створити».
 * Returns null when the gate is satisfied.
 */
export function firstUnmetRequirement({
  vendorName,
  payerId,
  lines,
}: CreateGateInput): string | null {
  if (vendorName.trim() === "") return "Додай назву витрати.";
  if (payerId == null) return "Вкажи, хто платив.";
  if (lines.some(isPartialLine)) {
    return "У кожної позиції має бути і назва, і сума.";
  }
  if (!lines.some(isValidLine)) return "Додай хоча б одну позицію з сумою.";
  return null;
}
